import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import moment from "moment";
import toast from "react-hot-toast";
import validSupergitHIS from "../../api/validSupergitHIS";
import TableLoading from "../../components/Shared/TableLoading";
import CustomTable from "../../components/Shared/CustomTable";
import CustomPagination from "../../components/Shared/CustomPagination";
import SharedTooltip from "../../components/Shared/SharedTooltip";
import HisInvoicesFilters from "./HisInvoicesFilters";
function HisCreditCompanyNotes() {
  const { t } = useTranslation();
  const currentDate = moment().format("YYYY-MM-DD");
  const firstDayOfCurrentMonth = moment().startOf("month").format("YYYY-MM-DD");

  const [filters, setFilters] = useState({
    start_date: firstDayOfCurrentMonth,
    end_date: currentDate,
    document_id: "",
    filters: {
      file_number: "",
      created_by: "",
      payment_method: "",
      invoice_number: "",
    },
    page: 1,
    query: "",
    per_page: 10,
  });
  const [data, setData] = useState({});
  const [loading, setLoading] = useState(false);
  const [filterLoading, setFilterLoading] = useState(false);
  const [showFilters, setShowFilters] = useState(false);

  const fetchData = async (payload = filters) => {
    setLoading(true);
    try {
      const response = await validSupergitHIS.post(
        "his/listCancelCompany",
        payload
      );
      setData(response?.data || {});
    } catch (error) {
      console.error(error);
      toast.error(t("Failed to fetch credit company notes"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const onPageChange = (page, rowsPerPage) => {
    const newFilters = { ...filters, page, per_page: rowsPerPage };
    setFilters(newFilters);
    fetchData(newFilters);
  };

  const handleAddFilters = async () => {
    if (filters?.start_date && !filters?.end_date) {
      toast.error(t("Please select an end date"));
      return;
    }
    if (filters?.end_date && !filters?.start_date) {
      toast.error(t("Please select a start date"));
      return;
    }
    const payload = { ...filters, page: 1 };
    setFilterLoading(true);
    await fetchData(payload);
    setFilterLoading(false);
    setShowFilters(true);
  };

  const handleResetFilters = () => {
    const resetFilters = {
      start_date: firstDayOfCurrentMonth,
      end_date: currentDate,
      document_id: "",
      filters: {
        file_number: "",
        created_by: "",
        payment_method: "",
        invoice_number: "",
      },
      page: 1,
      query: "",
      per_page: 10,
    };
    setFilters(resetFilters);
    fetchData(resetFilters);
    setShowFilters(false);
  };

  const formatAmount = (value) => {
    if (value == null) return 0;
    return value % 1 !== 0 ? value.toFixed(2) : value;
  };

  const columnsData = [
    {
      field: "invoice_number",
      header: t("Invoice Number"),
      style: { width: "8rem" },
    },
    {
      field: "created_at",
      header: t("Created At"),
      body: (rowData) =>
        moment(rowData?.created_at).format("DD-MM-YYYY HH:mm a"),
    },
    { field: "patient.full_name", header: t("Patient Name") },
    { field: "patient.file_no", header: t("File No") },
    { field: "patient.nationality", header: t("Nationality") },
    {
      field: "patient_share",
      header: t("Patient Amount"),
      body: (el) => formatAmount(el?.patient_share),
    },
    {
      field: "patient_vat",
      header: t("Patient VAT"),
      body: (el) => formatAmount(el?.patient_vat),
    },
    {
      field: "paid_amount",
      header: t("Patient Paid Amount"),
      body: (el) =>
        formatAmount((el?.patient_share || 0) + (el?.patient_vat || 0)),
    },
    { field: "payment_method", header: t("Payment Method") },
    { field: "created_by", header: t("Created By") },
    { field: "patient_status", header: t("Status") },
  ];

  return (
    <div>
      <div className="flex justify-between flex-col gap-3 sm:gap-0 sm:flex-row mb-3">
        <SharedTooltip
          content={t("Keep_track_of_your_invoices")}
          position="left"
          id="Items"
        />
        <span
          data-tooltip-id="Items"
          className="font-semibold text-black/70 text-xl mt-2"
        >
          {t("Credit Company Notes")}
          <span className="italic text-lg ml-1">
            <span className="text-xs font-bold italic">
              ( {data?.totalRecords || 0} {t("Entries")})
            </span>
          </span>
        </span>
      </div>
      <HisInvoicesFilters
        filters={filters}
        setFilters={setFilters}
        handleAddFilters={handleAddFilters}
        filterLoading={filterLoading}
        handleResetFilters={handleResetFilters}
        showFilters={showFilters}
      />
      <CustomTable
        data={data?.data || []}
        isLoading={loading}
        loadingIcon={<TableLoading />}
        columns={columnsData}
      />
      <CustomPagination
        currentPage={data?.page || 1}
        rowsPerPage={data?.per_page || 10}
        totalRecords={Math.ceil(
          (data?.totalRecords || 0) / (data?.per_page || 1)
        )}
        totalRecord={data?.totalRecords || 0}
        onPageChange={onPageChange}
      />
    </div>
  );
}

export default HisCreditCompanyNotes;
